import {
  useKeyboardEvent,
  Drawer,
  Spacer,
} from '@oliasoft-open-source/react-ui-library';
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import {
  TbClipboardData, TbFile, TbInfoCircle, TbHistory,
} from 'react-icons/tb';
import './data-viewer.css';
import { IObject } from 'common/interfaces/hierarchy-entities';
import { hasAssociatedData } from 'common/utils/is-multiple-tables';
import { EntityType } from 'common/enums/entity-enums';
import { DataViewer } from './data-viewer';
import { MetadataComponent } from '../information-tab/information-tab';
import { ProjectData } from '../project-data/project-data';
import { AssociatedData } from './associated-data-viewer/associated-data-viewer';
import { VersionHistory } from './version-history/version-history';
import { ModalType } from 'common/enums/information-enums';

const DrawerTabType = Object.freeze({
  Associated: 'associated',
  Data: 'data',
  Info: 'info',
  History: 'history',
});

type DataDrawerProps = {
  dataObject: IObject | null;
  archiveid: string;
  visible: boolean;
  setVisible: (bool: boolean) => void;
  setSelectedData: Dispatch<SetStateAction<IObject | null>>;
  setSelectedMultipleLinkedItems?: (data: IObject[], modalType: ModalType) => void;
  withPaste?: boolean;
};

export const DataDrawer = ({
  dataObject,
  visible,
  setVisible,
  setSelectedData,
  archiveid,
  setSelectedMultipleLinkedItems,
  withPaste = true,
}: DataDrawerProps) => {
  const [activeTabKey, setActiveTabKey] = useState<typeof DrawerTabType[keyof typeof DrawerTabType]>(DrawerTabType.Data);
  const designid = dataObject?.metadata?.hierarchyData?.design?.id;
  const [drawerWidth, setDrawerWidth] = useState(640);
  const maxDrawerWidth = window.innerWidth - 134;

  useKeyboardEvent('Escape', () => setVisible(false));

  useEffect(() => {
    if (!dataObject) {
      setVisible(false);
    }
  }, [dataObject]);

  const getTabs = () => {
    const tabs = [];
    if (hasAssociatedData(dataObject?.type as EntityType)) {
      tabs.unshift({
        key: DrawerTabType.Associated,
        content: (
          <div style={{
            padding: 'var(--padding)',
            width: drawerWidth - 32,
          }}
          >
            {dataObject && <AssociatedData objectid={dataObject?.objectid} nodeid={dataObject?.nodeid} designid={designid} entityType={dataObject?.type as EntityType} archiveid={archiveid} setSelectedData={setSelectedData} />}
          </div>),
        icon: <TbClipboardData />,
      });
    }
    return [
      ...tabs,
      {
        key: DrawerTabType.Data,
        content: <DataViewer
          dataObject={dataObject}
          width={drawerWidth}
          key={dataObject?.objectid}
          setSelectedData={setSelectedData}
          setSelectedMultipleLinkedItems={setSelectedMultipleLinkedItems}
          withPaste={withPaste!}
        />,
        icon: <TbFile />,
      },
      {
        key: DrawerTabType.Info,
        content: dataObject && (
          <div style={{
            padding: 'var(--padding)', width: drawerWidth - 32,
          }}
          >
            <ProjectData dataObject={dataObject} />
            <Spacer />
            <MetadataComponent dataObject={dataObject} />
          </div>
        ),
        icon: <TbInfoCircle />,
      },
      {
        key: DrawerTabType.History,
        content: dataObject && (
          <div style={{ padding: 'var(--padding)', width: drawerWidth - 32 }}>
            <VersionHistory objectid={dataObject.objectid} nodeid={dataObject.nodeid} entityType={dataObject.type} dataObject={dataObject} />
          </div>
        ),
        icon: <TbHistory />,
      },
    ];
  };

  const tabs = getTabs();
  const foundIndex = tabs.findIndex((tab) => tab.key === activeTabKey);
  const activeTabIndex = foundIndex !== -1 ? foundIndex : 0;

  return (
    <Drawer
      right
      width={drawerWidth}
      // @ts-ignore
      maxOpenWidth={maxDrawerWidth}
      onResize={width => {
        setDrawerWidth((width as number));
      }}
      open={visible}
      setOpen={setVisible}
      tabs={tabs}
      activeTab={activeTabIndex}
      setActiveTab={(index) => setActiveTabKey(tabs[index]?.key)}
    />
  );
};
